import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Group, Loader, Paper, SegmentedControl, SimpleGrid, Text, TextInput } from '@mantine/core';
import { InvoiceSetting } from '../types/invoice-setting.type';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { Booking } from '../types/BookingModel';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { config } from '../helpers/Config';
import { getAccessToken } from '../helpers/TokenRefresh';
import { generateAccountReportPdf, groupBookingsByPerson } from '../helpers/accountReportPdf';

interface PersonSummary {
  id: number;
  lastName: string;
  firstName: string;
  iban: string;
  net: number;
}

interface SourceAccountInfo {
  name: string;
  iban: string;
}

type SourceAccountMap = Record<number, SourceAccountInfo>;

type StatusFilter = 'all' | 'credit' | 'debit';

function fmt(v: number) {
  return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** Resolves which club payment account pays out to each of the given (outbound) persons. */
async function fetchSourceAccounts(personIds: number[]): Promise<SourceAccountMap> {
  if (personIds.length === 0) return {};
  try {
    const token = await getAccessToken();
    const response = await axios.get(`${config.apiUrl}/sepa/source-accounts?personIds=${personIds.join(',')}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data ?? {};
  } catch {
    return {};
  }
}

function statusBadge(net: number) {
  if (net < 0) return <Badge color="teal" variant="light">Guthaben</Badge>;
  if (net > 0) return <Badge color="orange" variant="light">Offen</Badge>;
  return <Badge color="gray" variant="light">Ausgeglichen</Badge>;
}

const PAGE_BOTTOM = 275;

function generateSummaryPdf(rows: PersonSummary[], sepaMaxAmount: number | null, sourceAccounts: SourceAccountMap) {
  const doc = new jsPDF();
  const today = dayjs().format('DD.MM.YYYY');
  const hasCap = sepaMaxAmount !== null;
  const colCount = hasCap ? 4 : 3;
  const head = hasCap ? [['Name', 'IBAN', 'Betrag', 'SEPA Betrag']] : [['Name', 'IBAN', 'Betrag']];

  doc.setFontSize(14);
  doc.text('Konten Zusammenfassung', 14, 16);
  doc.setFontSize(9);
  doc.text(`Stand: ${today}`, 14, 22);
  if (hasCap) doc.text(`SEPA-Limit: max. ${fmt(sepaMaxAmount!)} pro Person`, 14, 27);

  let y = hasCap ? 34 : 30;
  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_BOTTOM) {
      doc.addPage();
      y = 20;
    }
  };

  // renders one table (a source-account group, or the flat "Offene Beträge" list) and returns its net sum
  const renderTable = (groupRows: PersonSummary[], totalLabel: string) => {
    const body = groupRows.map((r) => {
      const capped = hasCap ? Math.min(Math.abs(r.net), sepaMaxAmount!) : null;
      const isCapped = hasCap && Math.abs(r.net) > sepaMaxAmount!;
      const row = [`${r.lastName} ${r.firstName}`, r.iban, fmt(r.net)];
      if (hasCap) row.push(capped !== null ? fmt(capped) + (isCapped ? ' *' : '') : '');
      return row;
    });
    const subtotal = groupRows.reduce((s, r) => s + r.net, 0);
    const cappedSubtotal = hasCap ? groupRows.reduce((s, r) => s + Math.min(Math.abs(r.net), sepaMaxAmount!), 0) : null;
    body.push(hasCap ? ['', totalLabel, fmt(subtotal), fmt(cappedSubtotal!)] : ['', totalLabel, fmt(subtotal)]);

    autoTable(doc, {
      startY: y,
      head,
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [60, 60, 60] },
      columnStyles: { [colCount - 1]: { halign: 'right' }, [colCount - 2]: { halign: 'right' } },
      didParseCell: (data) => {
        if (data.row.index === groupRows.length) data.cell.styles.fontStyle = 'bold';
      },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y + 20) + 8;
    return subtotal;
  };

  const outbound = rows.filter((r) => r.net < 0);
  const other = rows.filter((r) => r.net >= 0);

  if (outbound.length > 0) {
    ensureSpace(10);
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text('Auszahlungen (Guthaben) nach Quellkonto', 14, y + 6);
    y += 10;

    const groups = new Map<string, { label: string; rows: PersonSummary[] }>();
    for (const r of outbound) {
      const acc = sourceAccounts[r.id];
      const label = acc ? `${acc.name} (${acc.iban})` : 'Unbekanntes Konto';
      if (!groups.has(label)) groups.set(label, { label, rows: [] });
      groups.get(label)!.rows.push(r);
    }

    let outboundTotal = 0;
    for (const { label, rows: groupRows } of Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label))) {
      ensureSpace(20);
      doc.setFontSize(10);
      doc.setTextColor(80);
      doc.text(label, 14, y + 6);
      y += 10;
      outboundTotal += renderTable(groupRows, 'Zwischensumme');
    }

    ensureSpace(8);
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(`Gesamt Auszahlungen: ${fmt(outboundTotal)}`, 14, y);
    y += 12;
  }

  if (other.length > 0) {
    ensureSpace(14);
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text('Offene Beträge', 14, y + 6);
    y += 10;
    renderTable(other, 'Gesamt');
  }

  if (hasCap && outbound.length > 0) {
    ensureSpace(10);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('* Betrag wurde auf SEPA-Limit gekürzt', 14, y);
  }

  doc.save(`konten-zusammenfassung-${today}.pdf`);
}

function BookingReportSummary() {
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState<PersonSummary[]>([]);
  // All bookings, kept for the per-person account sheets (Kontoblätter).
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sepaLoading, setSepaLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sepaMaxAmount, setSepaMaxAmount] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasDownloaded = useRef(false);

  const personsParam = searchParams.get('persons');
  const personIds = useMemo(
    () => (personsParam ? new Set(personsParam.split(',').map(Number)) : null),
    [personsParam],
  );
  const autodownload = searchParams.get('autoprint') === '1';

  useEffect(() => {
    new HttpClient<InvoiceSetting>('/invoice-setting').getSingle()
      .then(s => setSepaMaxAmount(s?.sepaMaxAmountPerMonth ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const httpClient = new HttpClient<Booking>('/booking');
    httpClient.getAll().then((result) => {
      setBookings(result);
      const grouped = result.reduce((acc, booking) => {
        const id = booking.person?.id ?? -1;
        if (id === -1) return acc;
        if (!acc[id]) acc[id] = { person: booking.person, bookings: [] };
        acc[id].bookings.push(booking);
        return acc;
      }, {} as Record<number, { person: Booking['person']; bookings: Booking[] }>);

      const summaries: PersonSummary[] = Object.values(grouped)
        .map(({ person, bookings }) => {
          const creditSum = bookings
            .filter((b) => (b.bookingType?.multiplier ?? 0) > 0)
            .reduce((s, b) => s + b.bookingAmount, 0);
          const debitSum = bookings
            .filter((b) => (b.bookingType?.multiplier ?? 0) < 0)
            .reduce((s, b) => s + b.bookingAmount, 0);
          return {
            id: person?.id ?? -1,
            lastName: person?.lastName ?? '',
            firstName: person?.firstName ?? '',
            iban: person?.iban ?? '',
            net: creditSum - debitSum,
          };
        })
        .filter((s) => personIds ? personIds.has(s.id) : true)
        .sort((a, b) =>
          `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
        );

      setAllRows(summaries);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading && autodownload && !hasDownloaded.current) {
      hasDownloaded.current = true;
      const outboundIds = allRows.filter((r) => r.net < 0).map((r) => r.id);
      fetchSourceAccounts(outboundIds).then((accounts) => generateSummaryPdf(allRows, sepaMaxAmount, accounts));
    }
  }, [loading, autodownload]);

  // grand totals always reflect every account, independent of the active filter/search
  const totals = useMemo(() => {
    const creditRows = allRows.filter((r) => r.net < 0);
    const debitRows = allRows.filter((r) => r.net > 0);
    return {
      creditCount: creditRows.length,
      creditSum: creditRows.reduce((s, r) => s + Math.abs(r.net), 0),
      debitCount: debitRows.length,
      debitSum: debitRows.reduce((s, r) => s + r.net, 0),
    };
  }, [allRows]);

  const rows = useMemo(() => {
    // an explicit ?persons= deep-link (e.g. from Person list bulk actions) always shows exactly that selection
    if (personIds) return allRows;

    let filtered = allRows;
    if (statusFilter === 'credit') filtered = filtered.filter((r) => r.net < 0);
    else if (statusFilter === 'debit') filtered = filtered.filter((r) => r.net > 0);

    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((r) => `${r.lastName} ${r.firstName}`.toLowerCase().includes(term));
    }
    return filtered;
  }, [allRows, personIds, statusFilter, search]);

  // sums for just the checked rows, independent of the active filter/search
  const selectionTotals = useMemo(() => {
    const selected = allRows.filter((r) => selectedIds.has(r.id));
    const creditRows = selected.filter((r) => r.net < 0);
    const debitRows = selected.filter((r) => r.net > 0);
    const creditSum = creditRows.reduce((s, r) => s + Math.abs(r.net), 0);
    const debitSum = debitRows.reduce((s, r) => s + r.net, 0);
    return {
      count: selected.length,
      creditCount: creditRows.length,
      creditSum,
      debitCount: debitRows.length,
      debitSum,
      delta: debitSum - creditSum,
    };
  }, [allRows, selectedIds]);

  const downloadSepaCt = async () => {
    setSepaLoading(true);
    try {
      const token = await getAccessToken();
      const ids = Array.from(selectedIds).join(',');
      const response = await axios.get(`${config.apiUrl}/sepa/credit-transfer?personIds=${ids}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sepa-ct-${new Date().toISOString().slice(0, 10)}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const msg = err?.response?.data
        ? await err.response.data.text?.() ?? 'Unknown error'
        : err?.message ?? 'Unknown error';
      alert('SEPA export fehlgeschlagen: ' + msg);
    } finally {
      setSepaLoading(false);
    }
  };

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  if (loading) return <Loader size="md" />;

  const total = rows.reduce((s, r) => s + r.net, 0);
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const indeterminate = selectedIds.size > 0 && selectedIds.size < rows.length;

  return (
    <>
      <style>{'.account-row:hover { background-color: var(--mantine-color-default-hover); cursor: pointer; }'}</style>

      <Text fw={700} size="lg" mb="md">Konten-Übersicht</Text>

      <SimpleGrid cols={{ base: 1, xs: 2 }} mb="md">
        <Paper
          withBorder p="md" radius="md"
          onClick={() => setStatusFilter(statusFilter === 'credit' ? 'all' : 'credit')}
          style={{ cursor: 'pointer', borderColor: statusFilter === 'credit' ? 'var(--mantine-color-teal-6)' : undefined }}
        >
          <Text size="sm" c="dimmed">Guthaben gesamt ({totals.creditCount} Konten)</Text>
          <Text size="xl" fw={700} c="teal">{fmt(totals.creditSum)}</Text>
          {selectionTotals.creditCount > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              davon ausgewählt: {fmt(selectionTotals.creditSum)} ({selectionTotals.creditCount} Konten)
            </Text>
          )}
        </Paper>
        <Paper
          withBorder p="md" radius="md"
          onClick={() => setStatusFilter(statusFilter === 'debit' ? 'all' : 'debit')}
          style={{ cursor: 'pointer', borderColor: statusFilter === 'debit' ? 'var(--mantine-color-orange-6)' : undefined }}
        >
          <Text size="sm" c="dimmed">Offene Beträge gesamt ({totals.debitCount} Konten)</Text>
          <Text size="xl" fw={700} c="orange">{fmt(totals.debitSum)}</Text>
          {selectionTotals.debitCount > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              davon ausgewählt: {fmt(selectionTotals.debitSum)} ({selectionTotals.debitCount} Konten)
            </Text>
          )}
        </Paper>
      </SimpleGrid>

      {selectionTotals.count > 0 && (
        <Paper withBorder p="md" radius="md" mb="md">
          <Text size="sm" c="dimmed" mb={4}>Auswahl ({selectionTotals.count} Konten)</Text>
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed">Guthaben (ausgewählt)</Text>
              <Text fw={700} c="teal">{fmt(selectionTotals.creditSum)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Offene Beträge (ausgewählt)</Text>
              <Text fw={700} c="orange">{fmt(selectionTotals.debitSum)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Delta (Offen − Guthaben)</Text>
              <Text fw={700} c={selectionTotals.delta > 0 ? 'orange' : selectionTotals.delta < 0 ? 'teal' : 'gray'}>
                {fmt(selectionTotals.delta)}
              </Text>
            </div>
          </Group>
        </Paper>
      )}

      {!personIds && (
        <Group mb="md" justify="space-between" wrap="wrap">
          <SegmentedControl
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            data={[
              { value: 'all', label: 'Alle' },
              { value: 'credit', label: 'Guthaben' },
              { value: 'debit', label: 'Offene Beträge' },
            ]}
          />
          <TextInput
            placeholder="Name suchen…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', xs: 240 }}
          />
        </Group>
      )}

      <Group mb="md">
        <Button
          loading={pdfLoading}
          disabled={selectedIds.size === 0}
          onClick={async () => {
            const selected = rows.filter(r => selectedIds.has(r.id));
            setPdfLoading(true);
            try {
              const outboundIds = selected.filter(r => r.net < 0).map(r => r.id);
              const accounts = await fetchSourceAccounts(outboundIds);
              generateSummaryPdf(selected, sepaMaxAmount, accounts);
            } finally {
              setPdfLoading(false);
            }
          }}
        >Summenblatt (PDF)</Button>
        <Button
          variant="light"
          disabled={selectedIds.size === 0}
          onClick={() => {
            // One page per selected person, in the order of the list.
            const groups = groupBookingsByPerson(bookings.filter((b) => selectedIds.has(b.person?.id ?? -1)));
            generateAccountReportPdf(groups, `kontoblaetter-${dayjs().format('DD.MM.YYYY')}.pdf`);
          }}
        >Kontoblätter (PDF)</Button>
        <Button
          loading={sepaLoading}
          disabled={selectedIds.size === 0}
          onClick={downloadSepaCt}
        >
          SEPA Überweisung herunterladen
        </Button>
      </Group>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ccc', padding: '4px 8px', width: 36 }}>
              <Checkbox checked={allSelected} indeterminate={indeterminate} onChange={toggleAll} />
            </th>
            <th style={{ textAlign: 'left', border: '1px solid #ccc', padding: '4px 8px' }}>Name</th>
            <th style={{ textAlign: 'left', border: '1px solid #ccc', padding: '4px 8px' }}>IBAN</th>
            <th style={{ textAlign: 'left', border: '1px solid #ccc', padding: '4px 8px' }}>Status</th>
            <th style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>Betrag</th>
            {sepaMaxAmount !== null && (
              <th style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>
                SEPA Betrag <Badge size="xs" color="orange" variant="light">max {fmt(sepaMaxAmount)}</Badge>
              </th>
            )}
            <th style={{ border: '1px solid #ccc', padding: '4px 8px', width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="account-row"
              onClick={() => navigate(`/booking/report?persons=${r.id}`)}
            >
              <td style={{ border: '1px solid #ccc', padding: '4px 8px' }} onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={selectedIds.has(r.id)} onChange={() => toggleRow(r.id)} />
              </td>
              <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>
                <Link to={`/booking/report?persons=${r.id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'inherit' }}>
                  {r.lastName} {r.firstName}
                </Link>
              </td>
              <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{r.iban}</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{statusBadge(r.net)}</td>
              <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{fmt(r.net)}</td>
              {sepaMaxAmount !== null && (() => {
                if (r.net >= 0) return <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>—</td>;
                const capped = Math.min(Math.abs(r.net), sepaMaxAmount);
                const isCapped = Math.abs(r.net) > sepaMaxAmount;
                return (
                  <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>
                    {fmt(capped)}
                    {isCapped && <Badge size="xs" color="orange" variant="light" ml={4}>gekürzt</Badge>}
                  </td>
                );
              })()}
              <td style={{ border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center' }}>›</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 'bold' }}>
            <td style={{ border: '1px solid #ccc', padding: '4px 8px' }} />
            <td colSpan={3} style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>Gesamt</td>
            <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>{fmt(total)}</td>
            {sepaMaxAmount !== null && (
              <td style={{ textAlign: 'right', border: '1px solid #ccc', padding: '4px 8px' }}>
                {fmt(rows.filter(r => r.net < 0).reduce((s, r) => s + Math.min(Math.abs(r.net), sepaMaxAmount), 0))}
              </td>
            )}
            <td style={{ border: '1px solid #ccc', padding: '4px 8px' }} />
          </tr>
        </tbody>
      </table>

      {rows.length === 0 && <Text mt="md">Keine Einträge vorhanden.</Text>}
    </>
  );
}

export default BookingReportSummary;
